import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useNavigationType } from "react-router-dom";
import { FaFileSignature, FaSearch } from "react-icons/fa";
import Pagination from "Components/Pagination";
import CommonModal from "Components/CommonModal";
import PageContainer from "Components/UI/PageContainer";
import SearchCard from "Components/UI/SearchCard";
import Input from "Components/UI/Input";
import Select from "Components/UI/Select";
import Button from "Components/UI/Button";
import Badge from "Components/UI/Badge";
import { TableContainer, Table, TableHeader, TableCell } from "Components/UI/Table";
import { getPatentDesignRequestList } from "JS/API";
import { catchError, changeState, enterFn } from "JS/common";
import useQueryParams from "utils/useQueryParams";
import useSyncPageWithQuery from "utils/useSyncPageWithQuery";
import { designTokens, listPageStyles } from "utils/designSystem";
import {
  PATENT_DESIGN_STATUS,
  PATENT_DESIGN_STATUS_ORDER,
  PATENT_DESIGN_STATUS_STYLE,
  REQUEST_TYPE_FILTER_OPTIONS,
  formatDateTime,
  formatPrice,
  requestTypeLabel,
} from "utils/patentDesign";

const LIMIT = 20;

const TABLE_HEADERS = ["No", "신청일", "신청자", "유형", "제목", "첨부", "상태", "견적"];

const styles = {
  searchRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing.md,
    flexWrap: "wrap",
  },
  email: {
    fontSize: "12px",
    color: designTokens.colors.text.tertiary,
    marginTop: "2px",
  },
  title: { wordBreak: "break-all", lineHeight: 1.4 },
  submittedBadge: {
    marginLeft: designTokens.spacing.sm,
    backgroundColor: "#fff7ed",
    color: "#c2410c",
    whiteSpace: "nowrap",
  },
  nowrap: { whiteSpace: "nowrap" },
};

const readFilter = (searchParams) => ({
  status: searchParams.get("status") || "",
  requestType: searchParams.get("request_type") || "",
  keyword: searchParams.get("keyword") || "",
});

const isSameFilter = (a, b) =>
  a.status === b.status && a.requestType === b.requestType && a.keyword === b.keyword;

const PatentDesignRequest = () => {
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { searchParams, updateQueryParams } = useQueryParams();
  const legacyId = Number(searchParams.get("id")) || null;
  const [list, setList] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    page: Number(searchParams.get("page")) || 1,
    totalPage: 1,
    limit: LIMIT,
  });
  const [filter, setFilter] = useState(() => readFilter(searchParams));
  const [keywordInput, setKeywordInput] = useState(() => searchParams.get("keyword") || "");
  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });
  const requestIdRef = useRef(0);

  useSyncPageWithQuery(setPageInfo);

  useEffect(() => {
    if (legacyId) navigate(`/patent-design/${legacyId}`, { replace: true });
  }, [legacyId, navigate]);

  useEffect(() => {
    if (navigationType !== "POP") return;
    const next = readFilter(searchParams);
    setFilter((prev) => (isSameFilter(prev, next) ? prev : next));
    setKeywordInput((prev) => (prev === next.keyword ? prev : next.keyword));
  }, [searchParams, navigationType]);

  const handleError = useCallback(
    (result) => catchError(result, navigate, setAlertBox, setAlert),
    [navigate]
  );

  const fetchList = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const result = await getPatentDesignRequestList({
      page: pageInfo.page,
      limit: pageInfo.limit,
      status: filter.status,
      keyword: filter.keyword,
      requestType: filter.requestType,
    });
    if (requestIdRef.current !== requestId) return;
    if (typeof result === "object") {
      setList(result?.data?.data || []);
      changeState(setPageInfo, "totalPage", result?.data?.meta?.total_page || 1);
    } else return await handleError(result);
  }, [pageInfo.page, pageInfo.limit, filter.status, filter.keyword, filter.requestType, handleError]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    document.title = "마크픽 관리자 > 특허·디자인 신청";
  }, []);

  const applyFilter = (next) => {
    const merged = { ...filter, ...next };
    setFilter(merged);
    changeState(setPageInfo, "page", 1);
    updateQueryParams({
      status: merged.status,
      request_type: merged.requestType,
      keyword: merged.keyword,
      page: 1,
    });
  };

  const handleSearch = () => applyFilter({ keyword: keywordInput.trim() });

  const handleReset = () => {
    setKeywordInput("");
    applyFilter({ status: "", requestType: "", keyword: "" });
  };

  const hasFilter = Boolean(filter.status || filter.requestType || filter.keyword);

  return (
    <PageContainer title="특허·디자인 신청" icon={<FaFileSignature />}>
      <SearchCard>
        <div style={styles.searchRow}>
          <Select
            aria-label="상태 필터"
            value={filter.status}
            onChange={(e) => applyFilter({ status: e.target.value })}
            style={{ width: "180px" }}
          >
            <option value="">상태 전체</option>
            {PATENT_DESIGN_STATUS_ORDER.map((status) => (
              <option key={status} value={status}>
                {PATENT_DESIGN_STATUS[status]}
              </option>
            ))}
          </Select>
          <Select
            aria-label="유형 필터"
            value={filter.requestType}
            onChange={(e) => applyFilter({ requestType: e.target.value })}
            style={{ width: "150px" }}
          >
            <option value="">유형 전체</option>
            {REQUEST_TYPE_FILTER_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <Input
            type="text"
            placeholder="제목 · 신청자 이름 · 이메일 검색"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => {
              if (!alertBox.bool) enterFn(e, handleSearch);
            }}
            style={{ flex: 1, minWidth: "220px", width: "auto" }}
          />
          <Button variant="primary" icon={<FaSearch />} onClick={handleSearch}>
            검색
          </Button>
          {hasFilter && (
            <Button variant="secondary" onClick={handleReset}>
              초기화
            </Button>
          )}
        </div>
      </SearchCard>

      {list.length ? (
        <TableContainer>
          <Table>
            <colgroup>
              <col width="6%" />
              <col width="13%" />
              <col width="16%" />
              <col width="9%" />
              <col />
              <col width="6%" />
              <col width="12%" />
              <col width="10%" />
            </colgroup>
            <thead>
              <tr>
                {TABLE_HEADERS.map((label) => (
                  <TableHeader key={label}>{label}</TableHeader>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((row) => (
                <tr
                  key={row.id}
                  tabIndex={0}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(`/patent-design/${row.id}`, { state: { fromList: true } })
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      navigate(`/patent-design/${row.id}`, { state: { fromList: true } });
                    } else if (e.key === " ") {
                      e.preventDefault();
                      navigate(`/patent-design/${row.id}`, { state: { fromList: true } });
                    }
                  }}
                >
                  <TableCell style={{ fontWeight: "600" }}>{row.id}</TableCell>
                  <TableCell style={styles.nowrap}>{formatDateTime(row.created_at)}</TableCell>
                  <TableCell>
                    <div>{row.user?.name || "-"}</div>
                    {row.user?.email && <div style={styles.email}>{row.user.email}</div>}
                  </TableCell>
                  <TableCell style={styles.nowrap}>{requestTypeLabel(row.request_type)}</TableCell>
                  <TableCell style={styles.title}>
                    {row.title}
                    {row.has_submitted_documents && (
                      <Badge style={styles.submittedBadge}>서류 제출</Badge>
                    )}
                  </TableCell>
                  <TableCell>{row.file_count ?? 0}</TableCell>
                  <TableCell>
                    <Badge style={{ ...PATENT_DESIGN_STATUS_STYLE[row.status], ...styles.nowrap }}>
                      {PATENT_DESIGN_STATUS[row.status] || row.status}
                    </Badge>
                  </TableCell>
                  <TableCell style={styles.nowrap}>
                    {row.payment ? formatPrice(row.payment.merchant_total_price) : "-"}
                  </TableCell>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      ) : (
        <div style={listPageStyles.noDataContainer}>
          {hasFilter ? "검색 결과가 없습니다." : "신청 내역이 없습니다."}
        </div>
      )}

      <Pagination pageInfo={pageInfo} setPageInfo={setPageInfo} />

      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alert === "logout") navigate("/");
          }}
        />
      )}
    </PageContainer>
  );
};

export default PatentDesignRequest;
